import { createFileRoute } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/security")({
  head: () => ({
    meta: [
      { title: "Security — Recovery Trauma™" },
      {
        name: "description",
        content:
          "How Recovery Trauma™ protects your account and your data, including encryption, authentication, and access controls.",
      },
      { property: "og:title", content: "Security — Recovery Trauma™" },
      {
        property: "og:description",
        content: "Security practices used by Recovery Trauma™.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/security" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/security" },
    ],
  }),
  component: SecurityPage,
});

function SecurityPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> Security
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Security &amp; Data Protection
        </h1>
      </header>

      <div className="space-y-5 rounded-2xl border border-border bg-card p-6 text-sm leading-relaxed text-foreground/85">
        <h2 className="font-serif text-xl text-foreground">Encryption</h2>
        <ul className="list-disc space-y-2 ps-5">
          <li>
            <strong>In transit:</strong> all traffic to and from
            recoverytrauma.com is served over HTTPS/TLS.
          </li>
          <li>
            <strong>At rest:</strong> data is encrypted at rest by our managed
            database provider.
          </li>
        </ul>

        <h2 className="font-serif text-xl text-foreground">Authentication</h2>
        <ul className="list-disc space-y-2 ps-5">
          <li>
            Passwords are hashed and salted server-side; we never see your
            plain-text password.
          </li>
          <li>
            We check new and changed passwords against the Have I Been Pwned
            database to block known compromised passwords.
          </li>
        </ul>

        <h2 className="font-serif text-xl text-foreground">Access controls</h2>
        <ul className="list-disc space-y-2 ps-5">
          <li>
            Row-level security is enabled on every table that holds member
            data. Your journal, dream journal, check-ins, and saved items are
            visible only to you.
          </li>
          <li>
            Administrative roles are stored in a dedicated, separately gated
            table; ordinary accounts cannot escalate privileges.
          </li>
          <li>
            Service credentials never reach the browser bundle.
          </li>
        </ul>

        <h2 className="font-serif text-xl text-foreground">Responsible disclosure</h2>
        <p>
          Found a security issue? Please email{" "}
          <a className="underline" href="mailto:security@recoverytrauma.com">
            security@recoverytrauma.com
          </a>{" "}
          with details and steps to reproduce. We will acknowledge within 72
          hours.
        </p>
      </div>
    </div>
  );
}
